import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Divider,
  Collapse,
  Stack,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import WorkIcon from "@mui/icons-material/Work";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import baseURL from "../../config";
import Navbar from "../../Navbar/Navbar";

// ✅ Import Navbar

const VacancyBasedCrewCVs = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openCard, setOpenCard] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("You are not authorized to view this page");
      setLoading(false);
      return;
    }
    const fetchCrewCVs = async () => {
      try {
        const res = await axios.get(`${baseURL}/api/vacancyBasedCrewCVs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data.data);
      } catch (err) {
        setError("Failed to fetch crew CVs");
      } finally {
        setLoading(false);
      }
    };
    fetchCrewCVs();
  }, []);

  const filteredData = data.filter((item) => {
    const query = searchQuery.toLowerCase();
    return (
      item.crewName?.toLowerCase().includes(query) ||
      item.rank?.toLowerCase().includes(query) ||
      item.vacancyId?.title?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <>
        <Navbar />
        <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar />
        <Box p={3} textAlign="center" color="red">
          <Typography variant="h6">{error}</Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      {/* ✅ Navbar always at top */}
      <Navbar />

      <Box p={4}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ fontWeight: "bold", textAlign: "center", mb: 3, color: "#C30B12" }}
        >
          Vacancy Based Crew CVs
        </Typography>

        {/* 🔍 Search Bar */}
        <Box display="flex" justifyContent="center" mb={4}>
          <TextField
            variant="outlined"
            placeholder="Search by Crew Name, Rank, or Vacancy..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: "100%", maxWidth: 500 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {filteredData.length === 0 ? (
          <Typography align="center">No matching results</Typography>
        ) : (
          <Stack spacing={3}>
            {filteredData.map((item, idx) => (
              <Card
                key={idx}
                elevation={3}
                sx={{
                  borderRadius: 3,
                  overflow: "hidden",
                  transition: "0.3s",
                  "&:hover": { boxShadow: 8 },
                }}
              >
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    {item.vacancyId?.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {item.vacancyId?.vesselType} | {item.vacancyId?.contractDuration}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Box mb={2}>
                    <Chip icon={<WorkIcon />} label={item.rank || "N/A"} color="secondary" sx={{ mr: 1 }} />
                    <Chip label={item.crewName || "N/A"} variant="outlined" />
                  </Box>

                  <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                    <Chip
                      icon={<MonetizationOnIcon />}
                      label={`₹${item.vacancyId?.salary?.min} - ₹${item.vacancyId?.salary?.max} ${item.vacancyId?.salary?.currency}`}
                      color="success"
                    />
                    <Chip
                      icon={<LocationOnIcon />}
                      label={item.vacancyId?.location}
                      color="info"
                      variant="outlined"
                    />
                  </Stack>

                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Documents:
                  </Typography>
                  {item.crewCvId?.cvs?.length > 0 ? (
                    <Stack direction="row" flexWrap="wrap" spacing={1}>
                      {item.crewCvId.cvs.map((cv, i) => (
                        <Button
                          key={i}
                          startIcon={<PictureAsPdfIcon />}
                          size="small"
                          variant="outlined"
                          href={`http://localhost:8000/${cv.filePath.replace("\\", "/")}`}
                          target="_blank"
                          sx={{ mb: 1 }}
                        >
                          {cv.name || "View CV"}
                        </Button>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No CVs
                    </Typography>
                  )}
                </CardContent>

                <CardActions sx={{ justifyContent: "space-between", bgcolor: "#f9f9f9" }}>
                  <Button
                    size="small"
                    onClick={() => setOpenCard(openCard === idx ? null : idx)}
                    endIcon={openCard === idx ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  >
                    {openCard === idx ? "Hide Status" : "View Status"}
                  </Button>
                </CardActions>

                <Collapse in={openCard === idx} timeout="auto" unmountOnExit>
                  <Box p={2} bgcolor="#fafafa">
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Status History
                    </Typography>
                    {item.statusHistory?.length > 0 ? (
                      item.statusHistory.map((status, sIdx) => (
                        <Paper
                          key={sIdx}
                          sx={{ p: 1.5, mb: 1, borderRadius: 2, bgcolor: "#fff" }}
                          elevation={1}
                        >
                          <Typography variant="body2">
                            <b>{status.statusType}</b> – {status.remark}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {new Date(status.updatedAt).toLocaleString()}
                          </Typography>
                        </Paper>
                      ))
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No status history
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </Card>
            ))}
          </Stack>
        )}
      </Box>
    </>
  );
};

export default VacancyBasedCrewCVs;
